import { createI18n, I18nOptions, LocaleMessages, VueMessageType } from "vue-i18n";
import localeMessagesEn from "@/locales/en.json";
import localeMessagesEnPH from "@/locales/en-PH.json";
import localeMessagesES from "@/locales/es.json";
import localeMessagesEsCO from "@/locales/es-CO.json";
import localeMessagesEsMX from "@/locales/es-MX.json";
import localeMessagesEsPE from "@/locales/es-PE.json";
import localeMessagesFil from "@/locales/fil.json";
import localeMessagesCebPH from "@/locales/ceb-PH.json";
import localeMessagesFilPH from "@/locales/fil-PH.json";
import localeMessagesTlPH from "@/locales/tl-PH.json";
import localeMessagesPtBR from "@/locales/pt-BR.json";
import localeMessagesID from "@/locales/id.json";

class DefaultLocaleValues {
  static decimalFormat = { style: "decimal", minimumSignificantDigits: 0, maximumSignificantDigits: 2 } as const;
  static percentFormat = { style: "percent", useGrouping: false } as const;

  static philippinesNumberFormat = {
    currency: {
      style: "currency",
      currency: "PHP",
      useGrouping: true,
      currencyDisplay: "symbol",
      minimumFractionDigits: 2,
    },
    decimal: DefaultLocaleValues.decimalFormat,
    percent: DefaultLocaleValues.percentFormat,
  } as const;

  static standardDateTimeFormats = {
    narrow: { hour: "numeric" },
    short: { month: "short", day: "numeric" },
    medium: { weekday: "short", day: "numeric", month: "short" },
    long: { month: "short", day: "numeric", weekday: "short", hour: "numeric", minute: "numeric" },
    full: { year: "numeric", month: "short", day: "numeric", weekday: "short", hour: "numeric", minute: "numeric" },
  } as const;
}

function loadLocaleMessages(): { [x: string]: LocaleMessages<VueMessageType> } {
  /*
   * Values taken from src/locales.
   * Not as clean as the previous method, but it is more explicit and we do not need to mock require.context from webpack in order to run the tests using jest.
   * LOCALES NEED TO BE ADDED MANUALLY BY IMPORTING THE JSON AND THEN ADDING IT AS A KEY TO THE MESSAGES VARIABLE
   */
  const messages: { [x: string]: LocaleMessages<VueMessageType> } = {};
  messages["en"] = localeMessagesEn;
  messages["en-PH"] = localeMessagesEnPH;
  messages["es"] = localeMessagesES;
  messages["es-CO"] = localeMessagesEsCO;
  messages["es-MX"] = localeMessagesEsMX;
  messages["es-PE"] = localeMessagesEsPE;
  messages["fil"] = localeMessagesFil;
  messages["ceb-PH"] = localeMessagesCebPH;
  messages["fil-PH"] = localeMessagesFilPH;
  messages["tl-PH"] = localeMessagesTlPH;
  messages["pt-BR"] = localeMessagesPtBR;
  messages["id"] = localeMessagesID;
  // Extension-only locales — reuse closest base translation
  messages["en-ZA"] = localeMessagesEn;
  messages["es-PA"] = localeMessagesES;
  return messages;
}

// Message-locale keys the app can render. Sourced from loadLocaleMessages so it
// stays in sync with the bundled translations (single source of truth).
const SUPPORTED_LOCALES: readonly string[] = Object.keys(loadLocaleMessages());

/**
 * Resolves a requested language to a locale key the app has messages for.
 *
 * Resolution order:
 * 1. Exact match (case-insensitive).
 * 2. First supported locale sharing the language subtag (e.g. `es-AR` → `es`).
 * 3. Fallback: `"en"`.
 *
 * When `lang` is omitted the browser language (`window.navigator.language`, i.e.
 * the Accept-Language the browser would send) is used — so either an explicit
 * `?lang=` query param or the browser language drives the result.
 *
 * @param lang - BCP 47 locale tag (e.g. from the `?lang=` query param), or undefined.
 * @returns A locale key present in the bundled messages.
 */
function resolveSupportedLocale(lang?: string): string {
  const input = (lang ?? window.navigator.language ?? "").trim().toLowerCase();
  const exact = SUPPORTED_LOCALES.find((l) => l.toLowerCase() === input);
  if (exact) return exact;

  const langCode = input.split("-")[0];
  const byLang = SUPPORTED_LOCALES.find((l) => l.split("-")[0].toLowerCase() === langCode);
  if (byLang) return byLang;

  return "en";
}

function getFallbackLocale(locale: string) {
  // By language
  if (locale.includes("es")) return "es-MX";
  if (locale.includes("id")) return "id-ID";
  if (locale.includes("pt")) return "pt-BR";

  // By country
  if (locale.includes("BR")) return "pt-BR";
  if (locale.includes("CO")) return "es-CO";
  if (locale.includes("ID")) return "id-ID";
  if (locale.includes("MX")) return "es-MX";
  if (locale.includes("PE")) return "es-PE";
  if (locale.includes("PH")) return locale.includes("en") ? "en-PH" : "fil-PH";

  if (["fil", "tgl", "tl", "ceb"].some((lang) => locale.includes(lang))) return "fil-PH";

  return "en-US";
}

const i18nOptions: I18nOptions = {
  legacy: false,
  locale: window.navigator.language || "es-MX",
  fallbackLocale: "en",
  messages: loadLocaleMessages(),
  globalInjection: true,
  numberFormats: {
    "en-US": {
      currency: { style: "currency", currency: "USD", notation: "standard", minimumFractionDigits: 2 },
      decimal: { style: "decimal", minimumFractionDigits: 0, maximumFractionDigits: 2 },
      percent: DefaultLocaleValues.percentFormat,
    },
    // ************************************************************ BRAZIL
    "pt-BR": {
      currency: {
        style: "currency",
        currency: "BRL",
        useGrouping: true,
        currencyDisplay: "symbol",
        minimumFractionDigits: 2,
      },
      decimal: DefaultLocaleValues.decimalFormat,
      percent: DefaultLocaleValues.percentFormat,
    },
    // ************************************************************ COLOMBIA
    "es-CO": {
      currency: { style: "currency", currency: "COP", minimumFractionDigits: 0 },
    },
    // ************************************************************ INDONESIA
    id: {
      currency: { style: "currency", currency: "IDR", minimumFractionDigits: 0 },
    },
    "id-ID": {
      currency: { style: "currency", currency: "IDR", minimumFractionDigits: 0 },
    },
    // ************************************************************ MEXICO
    "es-MX": {
      currency: {
        style: "currency",
        currency: "MXN",
        useGrouping: true,
        currencyDisplay: "symbol",
        minimumFractionDigits: 0,
      },
      decimal: DefaultLocaleValues.decimalFormat,
      percent: DefaultLocaleValues.percentFormat,
    },
    // ************************************************************ PERU
    "es-PE": {
      currency: {
        style: "currency",
        currency: "PEN",
        useGrouping: true,
        currencyDisplay: "symbol",
        minimumFractionDigits: 2,
      },
      decimal: DefaultLocaleValues.decimalFormat,
      percent: DefaultLocaleValues.percentFormat,
    },
    // ************************************************************ PANAMA
    "es-PA": {
      currency: { style: "currency", currency: "USD", notation: "standard", minimumFractionDigits: 2 },
      decimal: DefaultLocaleValues.decimalFormat,
      percent: DefaultLocaleValues.percentFormat,
    },
    // ************************************************************ SOUTH AFRICA
    "en-ZA": {
      currency: { style: "currency", currency: "ZAR", notation: "standard", minimumFractionDigits: 2 },
      decimal: DefaultLocaleValues.decimalFormat,
      percent: DefaultLocaleValues.percentFormat,
    },
    // ************************************************************ PHILIPPINES
    "fil-PH": DefaultLocaleValues.philippinesNumberFormat,
    fil: DefaultLocaleValues.philippinesNumberFormat,
    "en-PH": DefaultLocaleValues.philippinesNumberFormat,
    "tl-PH": DefaultLocaleValues.philippinesNumberFormat,
    "ceb-PH": DefaultLocaleValues.philippinesNumberFormat,
  },
  datetimeFormats: {
    "en-US": DefaultLocaleValues.standardDateTimeFormats,
    "es-CO": DefaultLocaleValues.standardDateTimeFormats,
    "es-MX": DefaultLocaleValues.standardDateTimeFormats,
    "es-PE": DefaultLocaleValues.standardDateTimeFormats,
    id: { ...DefaultLocaleValues.standardDateTimeFormats, narrow: { hour: "numeric", minute: "numeric" } },
    "id-ID": { ...DefaultLocaleValues.standardDateTimeFormats, narrow: { hour: "numeric", minute: "numeric" } },
    "pt-BR": { ...DefaultLocaleValues.standardDateTimeFormats, narrow: { hour: "numeric", minute: "numeric" } },
    "en-ZA": DefaultLocaleValues.standardDateTimeFormats,
    "es-PA": DefaultLocaleValues.standardDateTimeFormats,
    "fil-PH": DefaultLocaleValues.standardDateTimeFormats,
    fil: DefaultLocaleValues.standardDateTimeFormats,
    "en-PH": DefaultLocaleValues.standardDateTimeFormats,
    "tl-PH": DefaultLocaleValues.standardDateTimeFormats,
    "ceb-PH": DefaultLocaleValues.standardDateTimeFormats,
  },
};

const i18n = createI18n<false>(i18nOptions);

function updateCurrencySymbol(locale: string, numberFormats: any, country: string) {
  if (numberFormats[locale]) {
    switch (country) {
      case "BR":
        numberFormats[locale].currency.currency = "BRL";
        numberFormats[locale].currency.style = "currency";
        numberFormats[locale].currency.useGrouping = true;
        numberFormats[locale].currency.currencyDisplay = "symbol";
        numberFormats[locale].currency.minimumFractionDigits = 2;
        break;

      case "CO":
        numberFormats[locale].currency.currency = "COP";
        numberFormats[locale].currency.style = "currency";
        numberFormats[locale].currency.useGrouping = true;
        numberFormats[locale].currency.currencyDisplay = "symbol";
        numberFormats[locale].currency.minimumFractionDigits = 2;
        break;

      case "ID":
        numberFormats[locale].currency.currency = "IDR";
        numberFormats[locale].currency.currencyDisplay = "symbol";
        break;

      case "MX":
        numberFormats[locale].currency.currency = "MXN";
        numberFormats[locale].currency.style = "currency";
        numberFormats[locale].currency.useGrouping = true;
        numberFormats[locale].currency.currencyDisplay = "symbol";
        numberFormats[locale].currency.minimumFractionDigits = 2;
        break;

      case "PH":
        numberFormats[locale].currency.currency = "PHP";
        numberFormats[locale].currency.style = "currency";
        numberFormats[locale].currency.useGrouping = true;
        numberFormats[locale].currency.currencyDisplay = "symbol";
        numberFormats[locale].currency.minimumFractionDigits = 2;
        break;
    }
  }
}

export { updateCurrencySymbol, getFallbackLocale, resolveSupportedLocale };
export default i18n;
